import type {
  CompletionBlocker,
  CompletionDashboardProjection,
  CompletionReadiness,
  CompletionWarning
} from '../types';

export type CompletionReadinessInput = {
  showCompletionHints: boolean;
  manualPenaltyCount: number;
  recordedPenaltyCount: number;
  unconfirmedExpenseCount: number;
  hasLogistCommentOnRoute: boolean;
  executionBlockers?: string[];
};

export function buildCompletionReadiness(input: CompletionReadinessInput): CompletionReadiness {
  const blockers: CompletionBlocker[] = [];
  const warnings: CompletionWarning[] = [];

  if (!input.showCompletionHints) {
    return {
      canComplete: false,
      blockers,
      warnings,
      readinessState: 'not_applicable'
    };
  }

  for (const code of input.executionBlockers ?? []) {
    blockers.push({ code, message: code });
  }

  if (input.manualPenaltyCount > 0 && input.recordedPenaltyCount === 0) {
    warnings.push({
      code: 'penalties_not_reviewed',
      message: 'Не проверены штрафы по договору перевозчика',
      severity: 'warning'
    });
  }

  if (input.unconfirmedExpenseCount > 0) {
    warnings.push({
      code: 'expenses_unconfirmed',
      message: `Расходы ожидают подтверждения бухгалтерией (${input.unconfirmedExpenseCount})`,
      severity: 'warning'
    });
  }

  if (!input.hasLogistCommentOnRoute) {
    warnings.push({
      code: 'logist_comment_pending',
      message: 'Комментарий логиста — при завершении маршрута',
      severity: 'info'
    });
  }

  const canComplete = blockers.length === 0;
  const readinessState =
    blockers.length > 0 ? 'blocked' : warnings.length > 0 ? 'warning' : 'ready';

  return { canComplete, blockers, warnings, readinessState };
}

export function buildCompletionDashboardProjection(
  input: CompletionReadinessInput
): CompletionDashboardProjection {
  const readiness = buildCompletionReadiness(input);
  return {
    showHints: input.showCompletionHints,
    readiness,
    pendingManualPenaltyCount: input.manualPenaltyCount,
    recordedPenaltyCount: input.recordedPenaltyCount,
    unconfirmedExpenseCount: input.unconfirmedExpenseCount
  };
}

/** Strip items for workspace UI (domain-driven, not modal internals). */
export function completionWarningsToStripItems(
  readiness: CompletionReadiness
): Array<{ id: string; label: string; severity: 'warning' | 'info' }> {
  if (readiness.readinessState === 'not_applicable') return [];
  return readiness.warnings.map((w) => ({
    id: w.code,
    label: w.message,
    severity: w.severity
  }));
}
